import React, { useState, useMemo } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useCarrierSession } from '../../context/CarrierSessionContext';
import type { SessionDetailItem, SessionDetailsResponse } from '../../services/carrierDownloadService';

const CarrierSessionDetailSidebarPanel: React.FC = () => {
  const { sessionPanel } = useCarrierSession();
  if (!sessionPanel) return null;

  const { loading, error, details } = sessionPanel;

  if (loading) {
    return (
      <div className="cdsl-drawer-skeleton" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="cdsl-skeleton-row" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px 20px' }}>
        <div className="cdsl-drawer-error">{error}</div>
      </div>
    );
  }

  if (!details) return null;

  return (
    <div style={{ padding: '12px 16px', overflow: 'auto', height: '100%' }}>
      <SessionDetailItems data={details} />
    </div>
  );
};

function SessionDetailItems({ data }: { data: SessionDetailsResponse }) {
  const items = data.Items ?? [];
  const allKeys = useMemo(() => new Set(items.map(i => i.RowNumber)), [items]);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set(items.map(i => i.RowNumber)));

  if (items.length === 0) return <div className="cdsl-drawer-empty">No detail records found.</div>;

  const allExpanded = expanded.size === allKeys.size;

  const toggle = (key: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(allKeys));

  return (
    <div className="cdsl-detail-items">
      <div className="cdsl-detail-toolbar">
        <span className="cdsl-detail-count">{items.length} record{items.length !== 1 ? 's' : ''}</span>
        <button className="cdsl-detail-toggle-all" onClick={toggleAll}>
          {allExpanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>
      {items.map(item => (
        <SessionDetailCard
          key={item.RowNumber}
          item={item}
          expanded={expanded.has(item.RowNumber)}
          onToggle={() => toggle(item.RowNumber)}
        />
      ))}
    </div>
  );
}

function SessionDetailCard({ item, expanded, onToggle }: { item: SessionDetailItem; expanded: boolean; onToggle: () => void }) {
  const fmt8 = (d: string) => d?.length === 8 ? `${d.slice(4,6)}/${d.slice(6,8)}/${d.slice(0,4)}` : d;
  const currency = (n: number | null) =>
    n === null || n === undefined ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const { Details } = item;
  return (
    <div className={`cdsl-detail-card${expanded ? '' : ' cdsl-detail-card--collapsed'}`}>
      <div className="cdsl-detail-card-header cdsl-detail-card-header--clickable" onClick={onToggle}>
        <div className="cdsl-detail-card-title">
          <span className="cdsl-detail-row-num">#{item.RowNumber}</span>
          <span className="cdsl-detail-tx-code">{item.Transaction}</span>
          <span className="cdsl-detail-tx-desc">{Details.TransactionDescription}</span>
        </div>
        <div className="cdsl-detail-card-header-right">
          <span className={`cdsl-status cdsl-status--${item.Consumed ? 'consumed' : 'not-consumed'}`}>
            {item.Consumed ? 'Merged' : 'Not Merged'}
          </span>
          <span className="cdsl-detail-chevron">
            {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </span>
        </div>
      </div>

      {expanded && (
        <>
          <div className="cdsl-detail-card-body">
            <div className="cdsl-detail-card-col">
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Insured</span>
                <span className="cdsl-detail-value">{item.InsuredName}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Address</span>
                <span className="cdsl-detail-value cdsl-detail-value--muted">{item.InsuredAddress}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Policy</span>
                <span className="cdsl-detail-value">{item.PolicyNumber}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Line of Business</span>
                <span className="cdsl-detail-value cdsl-detail-value--muted">{item.LineOfBusiness}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Carrier</span>
                <span className="cdsl-detail-value">{Details.CarrierHr.Name}</span>
              </div>
              {Details.Agent.Text && (
                <div className="cdsl-detail-field">
                  <span className="cdsl-detail-label">Agent</span>
                  <span className="cdsl-detail-value">{Details.Agent.Text}</span>
                </div>
              )}
            </div>
            <div className="cdsl-detail-card-col">
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Effective</span>
                <span className="cdsl-detail-value">{fmt8(item.EffectiveDate)}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Expiration</span>
                <span className="cdsl-detail-value">{fmt8(item.ExpirationDate)}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Tx Effective</span>
                <span className="cdsl-detail-value">{fmt8(item.TransactionEffectiveDate)}</span>
              </div>
              <div className="cdsl-detail-field">
                <span className="cdsl-detail-label">Premium</span>
                <span className="cdsl-detail-value">{currency(item.Premium)}</span>
              </div>
              {item.NonPremium !== null && (
                <div className="cdsl-detail-field">
                  <span className="cdsl-detail-label">Non-Premium</span>
                  <span className="cdsl-detail-value">{currency(item.NonPremium)}</span>
                </div>
              )}
              {Details.Action.Description && (
                <div className="cdsl-detail-field">
                  <span className="cdsl-detail-label">Action</span>
                  <span className="cdsl-detail-value cdsl-detail-value--muted">{Details.Action.Description}</span>
                </div>
              )}
            </div>
          </div>

          {item.DownloadLogs.length > 0 && (
            <div className="cdsl-detail-logs">
              <span className="cdsl-detail-logs-label">Download Logs</span>
              <ul className="cdsl-detail-logs-list">
                {item.DownloadLogs.map((log, i) => <li key={i}>{log}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CarrierSessionDetailSidebarPanel;